import React from 'react'
import Image from 'next/image'
import { useSiteContext, useResponsive } from '@dustin-web/microsite-components/src/hooks'
import { Site } from '@dustin-web/microsite-config/src/site-context'


type BannerImages = {
  desktop: string
  mobile: string
}


const bannerImages: Partial<Record<Site, BannerImages>> = {
  DustinSweden: {
    desktop: 'https://media.dustin.eu/content/qiodqeym/campaignbanner-desktop-se.png',
    mobile: 'https://media.dustin.eu/content/m0dgtmra/campaignbanner-mobile-se.png',
  },
  DustinNorway: {
    desktop: 'https://media.dustin.eu/content/ipakqz1z/campaignbanner-desktop-no.png',
    mobile: 'https://media.dustin.eu/content/v2bhjtza/campaignbanner-mobile-no.png',
  },
  DustinDenmark: {
    desktop: 'https://media.dustin.eu/content/hkljyoy4/campaignbanner-desktop-dk.png',
    mobile: 'https://media.dustin.eu/content/tmzhxirf/campaignbanner-mobile-dk.png',
  },
  DustinFinland: {
    desktop: 'https://media.dustin.eu/content/ypephpa1/campaignbanner-desktop-fi.png',
    mobile: 'https://media.dustin.eu/content/ugvfmvk4/campaignbanner-mobile-fi.png',
  },
  DustinNetherlands: {
    desktop: 'https://media.dustin.eu/content/nsclhaj2/campaignbanner-desktop-nl.png',
    mobile: 'https://media.dustin.eu/content/3fnoboz2/campaignbanner-mobile-nl.png',
  },
}

const laptopPathSlugs: Partial<Record<Site, string>> = {
  DustinSweden: 'barbara-datorer',
  DustinNorway: 'barbare-datamaskiner',
  DustinDenmark: 'barbare-computere',
  DustinFinland: 'kannettavat-tietokoneet',
  DustinNetherlands: 'laptops',
}

// Brands eligible for Spotlight banner
const spotlightBrands = ['dell', 'lenovo', 'microsoft', 'hp']

type Props = {
  activeFilters: { name: string; values: string[] }[]
  pathSlug: string
}

export const usePlpBannerVisible = (activeFilters: Props['activeFilters'], pathSlug: string) => {
  const { site } = useSiteContext()
  const laptopSlug = laptopPathSlugs[site]
  if (!laptopSlug || !pathSlug.endsWith(laptopSlug)) return false
  const manufacturerFilter = activeFilters.find(f => f.name === 'manufacturer')
  const hasSpotlightBrand = manufacturerFilter?.values?.some(v =>
    spotlightBrands.includes(v.toLowerCase())
  )
  if (manufacturerFilter && !hasSpotlightBrand) return false
  return !!bannerImages[site]
}

const PlpBrandBanner = ({ activeFilters, pathSlug }: Props) => {
  const { site } = useSiteContext()
  const { breakpoints } = useResponsive()

  const laptopSlug = laptopPathSlugs[site]

  if (!laptopSlug || !pathSlug.endsWith(laptopSlug)) {
    return null
  }

  const manufacturerFilter = activeFilters.find(f => f.name === 'manufacturer')
  const hasSpotlightBrand = manufacturerFilter?.values?.some(v =>
    spotlightBrands.includes(v.toLowerCase())
  )

  if (manufacturerFilter && !hasSpotlightBrand) {
    return null
  }

  const images = bannerImages[site]
  if (!images) return null

  const src = breakpoints.max.SM ? images.mobile : images.desktop

  return (
    <Image
      src={src}
      alt="Campaign banner for laptops"
      width={0}
      height={0}
      sizes="(max-width: 992px) 100vw, 42vw"
      style={{ width: '100%', height: 'auto', display: 'block' }}
      unoptimized
    />
  )
}

export default PlpBrandBanner
